import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { Api } from '@services/api.service';
import { AuthService } from '@services/auth.service';
import { Utils } from '@services/utils.service';
import { ToastService } from '@services/toast.service';
import { ICustomer } from '@common-interfaces/customer';
import { ICustomerHistoryItem } from '@common-interfaces/organization-customer-views';
import { PageHeaderComponent } from '../../../../components/shared/page-header/page-header.component';
import { StatCardComponent } from '../../../../components/shared/stat-card/stat-card.component';
import { EmptyStateComponent } from '../../../../components/shared/empty-state/empty-state.component';
import { CustomerFormComponent, CustomerFormValue } from '../customer-form/customer-form.component';
import { CustomerHistoryComponent } from '../customer-history/customer-history.component';
import { customerStats } from '@common-helpers/customer-stats';
import { debugLogger } from '@common-helpers/debug-logger';

interface VenueCustomerResponse {
    customer: ICustomer;
    history: ICustomerHistoryItem[];
    venue: { _id: string; name: string };
}

/** One venue's own record of an organisation customer. Edits here stay at this venue. */
@Component({
    selector: 'venue-customer',
    imports: [
        DatePipe,
        RouterLink,
        TranslatePipe,
        TabsModule,
        TagModule,
        ButtonModule,
        SkeletonModule,
        PageHeaderComponent,
        StatCardComponent,
        EmptyStateComponent,
        CustomerFormComponent,
        CustomerHistoryComponent
    ],
    templateUrl: './venue-customer.component.html'
})
export class VenueCustomerComponent {
    private api = inject(Api);
    private auth = inject(AuthService);
    private toast = inject(ToastService);
    private translate = inject(TranslateService);
    private confirm = inject(ConfirmationService);
    utility = inject(Utils);

    cid = input<string>('');
    clientId = input<string>('');

    orgId = this.auth.organizationId;
    loading = signal(true);
    saving = signal(false);
    syncing = signal(false);
    errorCode = signal<string | null>(null);
    selectedTab = signal('history');

    data = signal<VenueCustomerResponse | null>(null);
    stats = computed(() => customerStats(this.data()?.history ?? []));
    fullName = computed(() => {
        const c = this.data()?.customer;
        return c ? `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || '' : '';
    });
    backLink = computed(() => ['/organizations', this.orgId(), 'crm', 'customers', this.cid()]);

    constructor() {
        effect(() => {
            const cid = this.cid();
            const clientId = this.clientId();
            if (cid && clientId && this.orgId()) {
                untracked(() => this.load());
            }
        });
    }

    async save(value: CustomerFormValue): Promise<void> {
        this.saving.set(true);
        try {
            const res = await this.api.currentorganization().patch<ICustomer>(this.url(), { customer: value });
            if (res.success) {
                this.data.update((d) => (d ? { ...d, customer: res.data } : d));
                this.toast.showSuccess(this.translate.instant('CUSTOMER_SAVED'));
            }
        } catch (error) {
            this.showError(error);
        } finally {
            this.saving.set(false);
        }
    }

    confirmSync(): void {
        this.confirm.confirm({
            header: this.translate.instant('APPLY_ORG_PROFILE_TITLE'),
            message: this.translate.instant('APPLY_ORG_PROFILE_TEXT', { venue: this.data()?.venue.name ?? '' }),
            icon: 'ph ph-arrows-down-up',
            acceptLabel: this.translate.instant('APPLY'),
            rejectLabel: this.translate.instant('CANCEL'),
            rejectButtonStyleClass: 'p-button-text',
            accept: () => this.sync()
        });
    }

    private async sync(): Promise<void> {
        this.syncing.set(true);
        try {
            const res = await this.api.currentorganization().post<ICustomer>(`${this.url()}/sync-from-org`, {});
            if (res.success) {
                this.data.update((d) => (d ? { ...d, customer: res.data } : d));
                this.toast.showSuccess(this.translate.instant('ORG_PROFILE_APPLIED', { venue: this.data()?.venue.name ?? '' }));
            }
        } catch (error) {
            this.showError(error);
        } finally {
            this.syncing.set(false);
        }
    }

    private url(): string {
        return `/customers/${this.cid()}/venues/${this.clientId()}`;
    }

    private async load(): Promise<void> {
        this.loading.set(true);
        this.errorCode.set(null);
        try {
            const res = await this.api.currentorganization().get<VenueCustomerResponse>(this.url());
            if (res.success) {
                this.data.set(res.data);
            }
        } catch (error) {
            debugLogger.error('Failed to load venue customer', error);
            this.errorCode.set(error instanceof HttpErrorResponse && error.status === 403 ? 'NO_VENUE_ACCESS' : 'CUSTOMER_NOT_FOUND');
            this.data.set(null);
        } finally {
            this.loading.set(false);
        }
    }

    private showError(error: unknown): void {
        const code = error instanceof HttpErrorResponse ? error.error?.error : null;
        const key = code ? `ERR_${code}` : 'ERR_GENERIC';
        const message = this.translate.instant(key);
        this.toast.showError(message === key ? this.translate.instant('ERR_GENERIC') : message);
        debugLogger.error('Venue customer request failed', error);
    }
}
